import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EmptyState, Panel, Pill, PageContainer, PageHeader } from '../../components';
import { cx } from '../../lib/cx';
import { formatDate } from '../../lib/format';
import { useRequiredWorkspace } from '../workspace/useWorkspace';
import { monthEndQueryOptions } from './api';
import type { MonthEndEntry, PersonMonth, WorkType } from './types';

/**
 * Month end — turning logged hours into billing units for one calendar month.
 *
 * Reached only with `billing.read` (the nav entry and the route in
 * routes.tsx both gate on it; the API is the actual authority regardless).
 * There is no money anywhere on this screen, by decision — see the domain
 * package's header comment. Hours and units only; invoicing happens outside
 * Rundown, and rendering a currency symbol here would be the bug.
 */

const WORK_TYPE_LABELS: Readonly<Record<WorkType, string>> = {
  feature: 'Feature',
  change: 'Change',
  bug: 'Bug',
  support: 'Support',
  internal: 'Internal',
};

/** Units are already rounded once, server-side, by largest remainder — never re-round them here. */
function formatUnits(units: number): string {
  return units.toFixed(1);
}

function formatFactor(billingFactorBp: number): string {
  return `×${(billingFactorBp / 10_000).toFixed(2)}`;
}

/** `YYYY-MM`, the value a native `<input type="month">` reads and writes. */
function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function StatTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex min-w-[8.75rem] flex-1 flex-col gap-1 rounded-lg border border-line bg-surface-2 px-3.5 py-3">
      <span className="fs-11 text-muted">{label}</span>
      <span className="fs-22 leading-none font-bold tracking-[-0.01em] text-ink">{value}</span>
      {hint !== undefined && <span className="fs-11 text-muted">{hint}</span>}
    </div>
  );
}

function PersonRow({ person }: { person: PersonMonth }) {
  // More than one frozen factor means a mid-month grade (or factor) change —
  // worth flagging, because it is the exact case the freeze exists to handle
  // correctly rather than the case to be surprised by in a reconciliation.
  const factors = person.billingFactorsBp ?? [];
  const midMonthChange = factors.length > 1;

  return (
    <tr className="border-b border-line last:border-b-0">
      <td className="px-4 py-2.5 fs-13 font-medium text-ink">
        <div className="flex items-center gap-1.5">
          {person.userName}
          {midMonthChange && (
            <Pill tone="warn" title="Billing factor changed mid-month">
              factor changed
            </Pill>
          )}
        </div>
        {factors.length > 0 && (
          <div className="mt-0.5 fs-11 text-muted">
            {factors.map((factor) => formatFactor(factor)).join(', ')}
          </div>
        )}
      </td>
      <td className="px-4 py-2.5 text-right fs-13 text-ink-soft">{person.convertingHours.toFixed(1)}h</td>
      <td className="px-4 py-2.5 text-right fs-13 text-ink-soft">{person.bugHours.toFixed(1)}h</td>
      <td className="px-4 py-2.5 text-right fs-13 text-ink-soft">{person.supportHours.toFixed(1)}h</td>
      <td className="px-4 py-2.5 text-right fs-13 text-ink-soft">{person.internalHours.toFixed(1)}h</td>
      <td className="px-4 py-2.5 text-right fs-13 font-semibold text-ink">{formatUnits(person.units)}</td>
    </tr>
  );
}

function EntryRow({ entry }: { entry: MonthEndEntry }) {
  return (
    <tr className="border-b border-line last:border-b-0">
      <td className="px-4 py-2 fs-12.5 text-ink-soft whitespace-nowrap">{formatDate(entry.occurredOn)}</td>
      <td className="px-4 py-2 fs-12.5 text-ink">{entry.userName}</td>
      <td className="px-4 py-2 fs-12.5 text-ink-soft">{WORK_TYPE_LABELS[entry.type]}</td>
      <td className="px-4 py-2 text-right fs-12.5 text-ink-soft">{entry.hours.toFixed(1)}h</td>
      <td className="px-4 py-2 text-right fs-12.5 text-muted">
        {entry.billingFactorBp !== undefined ? formatFactor(entry.billingFactorBp) : '—'}
      </td>
      <td className="max-w-[17.5rem] truncate px-4 py-2 fs-12.5 text-muted" title={entry.note ?? undefined}>
        {entry.note ?? '—'}
      </td>
    </tr>
  );
}

export function MonthEndPage() {
  const { workspace } = useRequiredWorkspace();
  const [month, setMonth] = useState(currentMonth);
  const query = useQuery(monthEndQueryOptions(workspace.id, month));
  const summary = query.data;

  const peopleSum = useMemo(
    () => (summary === undefined ? 0 : summary.people.reduce((sum, e) => sum + e.units, 0)),
    [summary],
  );

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Month end"
        description="Logged hours turned into billing units. Hours and units only — invoicing happens outside Rundown."
        actions={
          <label className="flex flex-col gap-1">
            <span className="fs-11 text-muted">Month</span>
            <input
              type="month"
              value={month}
              onChange={(event) => setMonth(event.target.value)}
              className="rounded-[9px] border border-line bg-surface-2 px-3 py-2 fs-13 text-ink focus:bg-base"
            />
          </label>
        }
      />

      {query.isPending ? (
        <p className="fs-12 text-muted">Loading…</p>
      ) : query.error !== null ? (
        <EmptyState title="Could not load Month end" description={query.error.message} />
      ) : summary === undefined ? null : (
        <div className="flex flex-col gap-5">
          {/* --- Headline + included-credit position --------------------- */}
          <div className="flex flex-wrap gap-3">
            <StatTile label="Total units this month" value={formatUnits(summary.unitsTotal)} />
            <StatTile
              label="Included in retainer"
              value={formatUnits(summary.includedCreditUnits)}
              hint="workspace setting"
            />
            <StatTile
              label={summary.billedExcessUnits > 0 ? 'Beyond the included credit' : 'Headroom remaining'}
              value={formatUnits(
                summary.billedExcessUnits > 0
                  ? summary.billedExcessUnits
                  : Math.max(0, summary.includedCreditUnits - summary.unitsTotal),
              )}
              hint={
                summary.billedExcessUnits > 0
                  ? 'the only figure that would ever be invoiced'
                  : 'still within the retainer'
              }
            />
          </div>

          {/* --- By work type ---------------------------------------------- */}
          <Panel title="By work type">
            <div className="flex flex-wrap gap-3">
              <StatTile
                label="Feature + Change"
                value={`${summary.byWorkType.convertingHours.toFixed(1)}h`}
                hint={`${formatUnits(summary.byWorkType.convertingUnits)} units`}
              />
              <StatTile label="Bug (retainer)" value={`${summary.byWorkType.bugHours.toFixed(1)}h`} />
              <StatTile label="Support (retainer)" value={`${summary.byWorkType.supportHours.toFixed(1)}h`} />
              <StatTile label="Internal" value={`${summary.byWorkType.internalHours.toFixed(1)}h`} />
            </div>
          </Panel>

          {/* --- By person ---------------------------------------------- */}
          <Panel title="By person" flush>
            {summary.people.length === 0 ? (
              <div className="px-4 py-6">
                <EmptyState
                  title="No hours logged"
                  description="Nothing was logged against this month yet."
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-line text-left fs-11 text-muted">
                      <th className="px-4 py-2 font-medium">Person</th>
                      <th className="px-4 py-2 text-right font-medium">Feature + Change</th>
                      <th className="px-4 py-2 text-right font-medium">Bug</th>
                      <th className="px-4 py-2 text-right font-medium">Support</th>
                      <th className="px-4 py-2 text-right font-medium">Internal</th>
                      <th className="px-4 py-2 text-right font-medium">Units</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.people.map((person) => (
                      <PersonRow key={person.userId} person={person} />
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="px-4 py-2.5 fs-11 text-muted" colSpan={5}>
                        Sum of the rows above
                      </td>
                      <td
                        className={cx(
                          'px-4 py-2.5 text-right fs-13 font-bold',
                          // Compare at the same 1dp the server rounded to, not by strict
                          // float equality — summing display-precision floats in JS can
                          // drift by float epsilon even when both sides were produced by
                          // the same apportionment. This is a display-only sanity check;
                          // the server's arithmetic is integer throughout, so a genuine
                          // mismatch here would mean a real bug, not float noise.
                          Math.round(peopleSum * 10) === Math.round(summary.unitsTotal * 10)
                            ? 'text-ink'
                            : 'text-danger',
                        )}
                      >
                        {formatUnits(peopleSum)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Panel>

          {/* --- Underlying entries --------------------------------------- */}
          <Panel title="Entries" flush>
            {summary.entries.length === 0 ? (
              <div className="px-4 py-6">
                <EmptyState title="No entries" description="No time was logged in this month." />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-line text-left fs-11 text-muted">
                      <th className="px-4 py-2 font-medium">Date</th>
                      <th className="px-4 py-2 font-medium">Person</th>
                      <th className="px-4 py-2 font-medium">Type</th>
                      <th className="px-4 py-2 text-right font-medium">Hours</th>
                      <th className="px-4 py-2 text-right font-medium">Factor</th>
                      <th className="px-4 py-2 font-medium">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.entries.map((entry) => (
                      <EntryRow key={entry.id} entry={entry} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>
      )}
    </PageContainer>
  );
}
